/**
 * Event-based tracking — operator UI (feat #169).
 *
 * Composition:
 *   - Header with emerald→sky gradient headline.
 *   - Stats overview card aggregating counts + total value per event name.
 *   - Event definitions card: create, archive, activate.
 *   - Capture-test card: send a synthetic event for a definition.
 *   - Recent events table: last 50, newest first.
 *
 * Brand: emerald→sky gradient headline, zinc borders, 6-8px rounded, light
 * theme, no emojis, no competitor names.
 */
import {
	useCallback,
	useMemo,
	useState,
	type FormEvent,
} from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	activateDefinition,
	archiveDefinition,
	captureEvent,
	CATEGORIES,
	categoryLabel,
	createDefinition,
	eventTrackingQueryKeys,
	fetchStats,
	formatCents,
	formatDate,
	formatEventCount,
	listDefinitions,
	listEvents,
	validateCategory,
	validateCurrency,
	validateDescription,
	validateEventName,
	validateIdLike,
	validateValueCents,
	type CustomEvent,
	type EventCategory,
	type EventDefinition,
	type EventStatRow,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-600 bg-green-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-green-700 hover:border-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function EventTrackingPage() {
	const queryClient = useQueryClient();

	const definitionsQuery = useQuery({
		queryKey: eventTrackingQueryKeys.definitions(),
		queryFn: listDefinitions,
	});

	const eventsQuery = useQuery({
		queryKey: eventTrackingQueryKeys.events(),
		queryFn: () => listEvents(50),
	});

	const statsQuery = useQuery({
		queryKey: eventTrackingQueryKeys.stats(),
		queryFn: fetchStats,
	});

	const invalidateAll = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: eventTrackingQueryKeys.all });
	}, [queryClient]);

	const createMutation = useMutation({
		mutationFn: createDefinition,
		onSuccess: (def) => {
			toast.success(`Event "${def.event_name}" registered.`);
			invalidateAll();
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to register event.");
		},
	});

	const archiveMutation = useMutation({
		mutationFn: archiveDefinition,
		onSuccess: () => {
			toast.success("Definition archived.");
			invalidateAll();
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to archive.");
		},
	});

	const activateMutation = useMutation({
		mutationFn: activateDefinition,
		onSuccess: () => {
			toast.success("Definition activated.");
			invalidateAll();
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to activate.");
		},
	});

	const captureMutation = useMutation({
		mutationFn: captureEvent,
		onSuccess: (evt) => {
			toast.success(`Event "${evt.event_name}" captured.`);
			invalidateAll();
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to capture event.");
		},
	});

	const definitions: readonly EventDefinition[] =
		definitionsQuery.data?.items ?? [];
	const events: readonly CustomEvent[] = eventsQuery.data?.items ?? [];
	const stats: readonly EventStatRow[] = statsQuery.data?.stats ?? [];

	const activeDefinitions = useMemo(
		() => definitions.filter((d) => d.status === "active"),
		[definitions],
	);

	return (
		<main
			data-testid="event-tracking-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />
			<StatsCard
				stats={stats}
				loading={statsQuery.isLoading}
				error={statsQuery.error?.message ?? null}
			/>
			<CreateDefinitionCard
				onSubmit={(args) => createMutation.mutate(args)}
				busy={createMutation.isPending}
			/>
			<DefinitionsCard
				rows={definitions}
				loading={definitionsQuery.isLoading}
				error={definitionsQuery.error?.message ?? null}
				onArchive={(id) => archiveMutation.mutate(id)}
				onActivate={(id) => activateMutation.mutate(id)}
				archiving={archiveMutation.isPending}
				activating={activateMutation.isPending}
			/>
			<CaptureCard
				activeDefinitions={activeDefinitions}
				onSubmit={(args) => captureMutation.mutate(args)}
				busy={captureMutation.isPending}
			/>
			<RecentEventsCard
				rows={events}
				loading={eventsQuery.isLoading}
				error={eventsQuery.error?.message ?? null}
			/>
		</main>
	);
}

export default EventTrackingPage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Tracking
			</p>
			<h1 className="text-green-700 text-3xl font-semibold tracking-tight sm:text-4xl">
				Event tracking
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Capture named events beyond clicks and conversions. Register the event
				names your integrations emit, then send{" "}
				<code className="rounded-[6px] bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-800">
					add_to_cart
				</code>
				,{" "}
				<code className="rounded-[6px] bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-800">
					purchase
				</code>
				,{" "}
				<code className="rounded-[6px] bg-zinc-100 px-1.5 py-0.5 text-xs text-zinc-800">
					signup
				</code>
				, or any custom name to the capture endpoint.
			</p>
		</header>
	);
}

function StatsCard(props: {
	stats: readonly EventStatRow[];
	loading: boolean;
	error: string | null;
}) {
	const total = props.stats.reduce((sum, s) => sum + s.count, 0);
	const totalValueCents = props.stats.reduce(
		(sum, s) => sum + s.total_value_cents,
		0,
	);

	return (
		<section className={`${CARD} p-5`} aria-label="Event stats">
			<header className="flex flex-col gap-1">
				<h2 className="text-base font-semibold text-zinc-900">Overview</h2>
				<p className={HINT}>
					Aggregated counts and revenue totals across all tracked events.
				</p>
			</header>
			{props.error ? (
				<p className="mt-4 text-sm text-red-600" role="alert">
					{props.error}
				</p>
			) : props.loading ? (
				<p className="mt-4 text-sm text-zinc-500">Loading stats.</p>
			) : props.stats.length === 0 ? (
				<p className="mt-4 text-sm text-zinc-500">
					No events captured yet. Register a definition and send your first
					event below.
				</p>
			) : (
				<div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
					<StatTile label="Total events" value={formatEventCount(total)} />
					<StatTile
						label="Total value"
						value={formatCents(totalValueCents, "USD")}
					/>
					<StatTile
						label="Event types"
						value={`${props.stats.length}`}
					/>
					<StatTile
						label="Top event"
						value={props.stats[0]?.event_name ?? "—"}
					/>
				</div>
			)}
			{props.stats.length > 0 ? (
				<div className="mt-5 overflow-x-auto">
					<table
						className="w-full border-collapse text-left text-sm"
						data-testid="stats-table"
					>
						<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
							<tr>
								<Th>Event name</Th>
								<Th className="text-right">Count</Th>
								<Th className="text-right">Total value</Th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{props.stats.map((row) => (
								<tr
									key={row.event_name}
									data-testid={`stat-row-${row.event_name}`}
									className="hover:bg-zinc-50/80"
								>
									<Td>
										<code className="font-mono text-zinc-900">
											{row.event_name}
										</code>
									</Td>
									<Td className="text-right text-zinc-900">
										{formatEventCount(row.count)}
									</Td>
									<Td className="text-right text-zinc-900">
										{formatCents(row.total_value_cents, "USD")}
									</Td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : null}
		</section>
	);
}

function StatTile(props: { label: string; value: string }) {
	return (
		<div className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
			<p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
				{props.label}
			</p>
			<p className="mt-1 text-base font-semibold text-zinc-900">
				{props.value}
			</p>
		</div>
	);
}

function CreateDefinitionCard(props: {
	onSubmit: (args: {
		event_name: string;
		category: EventCategory;
		description: string | null;
		value_enabled: boolean;
	}) => void;
	busy: boolean;
}) {
	const [name, setName] = useState("");
	const [category, setCategory] = useState<EventCategory>("custom");
	const [description, setDescription] = useState("");
	const [valueEnabled, setValueEnabled] = useState(false);
	const [errors, setErrors] = useState<{
		name?: string;
		category?: string;
		description?: string;
	}>({});

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const fieldErrors: typeof errors = {};

		const validName = validateEventName(name);
		if (!validName)
			fieldErrors.name =
				"Use snake_case, 1-64 chars. Must start with a letter.";

		const validCategory = validateCategory(category);
		if (!validCategory) fieldErrors.category = "Pick a valid category.";

		const validDescription = validateDescription(description);
		if (validDescription === "invalid")
			fieldErrors.description = "Keep it under 500 characters.";

		setErrors(fieldErrors);
		if (Object.keys(fieldErrors).length > 0) return;

		props.onSubmit({
			event_name: validName!,
			category: validCategory!,
			description:
				validDescription === "invalid" ? null : validDescription,
			value_enabled: valueEnabled,
		});
		setName("");
		setDescription("");
	};

	return (
		<section className={`${CARD} p-5`} aria-label="Create event definition">
			<h2 className="text-base font-semibold text-zinc-900">
				Register an event
			</h2>
			<p className={HINT}>
				Each tenant keeps its own catalog of event names.
			</p>
			<form
				onSubmit={handleSubmit}
				className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
				data-testid="create-definition-form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="event-name" className={LABEL}>
						Event name
					</label>
					<input
						id="event-name"
						name="event_name"
						type="text"
						required
						maxLength={64}
						className={INPUT}
						value={name}
						onChange={(e) => setName(e.currentTarget.value)}
						disabled={props.busy}
						placeholder="add_to_cart"
						data-testid="event-name-input"
					/>
					{errors.name ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.name}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="event-category" className={LABEL}>
						Category
					</label>
					<select
						id="event-category"
						name="category"
						className={INPUT}
						value={category}
						onChange={(e) =>
							setCategory(e.currentTarget.value as EventCategory)
						}
						disabled={props.busy}
						data-testid="event-category-select"
					>
						{CATEGORIES.map((c) => (
							<option key={c} value={c}>
								{categoryLabel(c)}
							</option>
						))}
					</select>
					{errors.category ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.category}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5 sm:col-span-2">
					<label htmlFor="event-description" className={LABEL}>
						Description (optional)
					</label>
					<textarea
						id="event-description"
						name="description"
						rows={2}
						maxLength={500}
						className={INPUT}
						value={description}
						onChange={(e) => setDescription(e.currentTarget.value)}
						disabled={props.busy}
						data-testid="event-description-input"
					/>
					{errors.description ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.description}
						</p>
					) : null}
				</div>

				<div className="sm:col-span-2">
					<label className="inline-flex items-center gap-2 text-sm text-zinc-700">
						<input
							type="checkbox"
							checked={valueEnabled}
							onChange={(e) => setValueEnabled(e.currentTarget.checked)}
							className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-500"
							disabled={props.busy}
							data-testid="event-value-toggle"
						/>
						<span>Carries a monetary value</span>
					</label>
				</div>

				<div className="sm:col-span-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={props.busy}
						data-testid="create-definition-submit"
					>
						{props.busy ? "Registering." : "Register event"}
					</button>
				</div>
			</form>
		</section>
	);
}

function DefinitionsCard(props: {
	rows: readonly EventDefinition[];
	loading: boolean;
	error: string | null;
	onArchive: (id: string) => void;
	onActivate: (id: string) => void;
	archiving: boolean;
	activating: boolean;
}) {
	return (
		<section className={`${CARD} flex flex-col`} aria-label="Event definitions">
			<header className="flex flex-col gap-1 border-b border-zinc-200 p-5">
				<h2 className="text-base font-semibold text-zinc-900">
					Registered events
				</h2>
				<p className={HINT}>
					{props.rows.length === 0
						? "No events registered yet."
						: `${props.rows.length} event definition${props.rows.length === 1 ? "" : "s"}.`}
				</p>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading definitions.</div>
			) : props.rows.length === 0 ? (
				<div className="flex flex-col items-center gap-1 p-10 text-center">
					<p className="text-sm font-medium text-zinc-700">
						No definitions yet.
					</p>
					<p className="text-xs text-zinc-500">
						Register an event above to start tracking it.
					</p>
				</div>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse text-left text-sm"
						data-testid="definitions-table"
					>
						<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
							<tr>
								<Th>Event name</Th>
								<Th>Category</Th>
								<Th>Status</Th>
								<Th>Value</Th>
								<Th>Created</Th>
								<Th className="text-right">Actions</Th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{props.rows.map((row) => (
								<tr
									key={row.id}
									data-testid={`definition-row-${row.id}`}
									className="hover:bg-zinc-50/80"
								>
									<Td>
										<code className="font-mono text-zinc-900">
											{row.event_name}
										</code>
										{row.description ? (
											<p className="mt-1 text-xs text-zinc-500">
												{row.description}
											</p>
										) : null}
									</Td>
									<Td>
										<CategoryBadge category={row.category} />
									</Td>
									<Td>
										<StatusBadge status={row.status} />
									</Td>
									<Td>
										<span className="text-zinc-700">
											{row.value_enabled ? "Enabled" : "—"}
										</span>
									</Td>
									<Td>
										<span className="text-zinc-600">
											{formatDate(row.created_at)}
										</span>
									</Td>
									<Td className="text-right">
										{row.status === "active" ? (
											<button
												type="button"
												onClick={() => props.onArchive(row.id)}
												className={BTN_NEUTRAL}
												disabled={props.archiving}
												data-testid={`archive-${row.id}`}
											>
												Archive
											</button>
										) : (
											<button
												type="button"
												onClick={() => props.onActivate(row.id)}
												className={BTN_NEUTRAL}
												disabled={props.activating}
												data-testid={`activate-${row.id}`}
											>
												Activate
											</button>
										)}
									</Td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

function CaptureCard(props: {
	activeDefinitions: readonly EventDefinition[];
	onSubmit: (args: {
		event_name: string;
		click_id?: string | null;
		affiliate_id?: string | null;
		value_cents?: number | null;
		currency?: string | null;
	}) => void;
	busy: boolean;
}) {
	const [eventName, setEventName] = useState("");
	const [clickId, setClickId] = useState("");
	const [affiliateId, setAffiliateId] = useState("");
	const [valueCents, setValueCents] = useState("");
	const [currency, setCurrency] = useState("");
	const [errors, setErrors] = useState<{
		event_name?: string;
		click_id?: string;
		affiliate_id?: string;
		value_cents?: string;
		currency?: string;
	}>({});

	const disabled = props.busy || props.activeDefinitions.length === 0;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const fieldErrors: typeof errors = {};

		const validName = validateEventName(eventName);
		if (!validName) fieldErrors.event_name = "Pick a registered event.";

		const validClick = validateIdLike(clickId);
		if (validClick === "invalid")
			fieldErrors.click_id = "Only letters, digits, underscore, dash (<=128).";

		const validAff = validateIdLike(affiliateId);
		if (validAff === "invalid")
			fieldErrors.affiliate_id =
				"Only letters, digits, underscore, dash (<=128).";

		const validValue = validateValueCents(valueCents);
		if (validValue === "invalid")
			fieldErrors.value_cents = "Non-negative integer cents.";

		const validCurrency = validateCurrency(currency);
		if (validCurrency === "invalid")
			fieldErrors.currency = "3-letter ISO-4217 code (e.g. usd).";

		setErrors(fieldErrors);
		if (Object.keys(fieldErrors).length > 0) return;

		props.onSubmit({
			event_name: validName!,
			click_id: validClick === "invalid" ? null : validClick,
			affiliate_id: validAff === "invalid" ? null : validAff,
			value_cents: validValue === "invalid" ? null : validValue,
			currency: validCurrency === "invalid" ? null : validCurrency,
		});
		setClickId("");
		setAffiliateId("");
		setValueCents("");
		setCurrency("");
	};

	return (
		<section className={`${CARD} p-5`} aria-label="Capture event">
			<h2 className="text-base font-semibold text-zinc-900">
				Send a test event
			</h2>
			<p className={HINT}>
				{props.activeDefinitions.length === 0
					? "Register an active event first to send instances."
					: "Fire a synthetic event to validate your integration pipeline."}
			</p>
			<form
				onSubmit={handleSubmit}
				className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
				data-testid="capture-form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="capture-name" className={LABEL}>
						Event name
					</label>
					<select
						id="capture-name"
						name="event_name"
						className={INPUT}
						value={eventName}
						onChange={(e) => setEventName(e.currentTarget.value)}
						disabled={disabled}
						data-testid="capture-name-select"
					>
						<option value="">Select an event…</option>
						{props.activeDefinitions.map((d) => (
							<option key={d.id} value={d.event_name}>
								{d.event_name}
							</option>
						))}
					</select>
					{errors.event_name ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.event_name}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="capture-click" className={LABEL}>
						Click id (optional)
					</label>
					<input
						id="capture-click"
						name="click_id"
						type="text"
						maxLength={128}
						className={INPUT}
						value={clickId}
						onChange={(e) => setClickId(e.currentTarget.value)}
						disabled={disabled}
						data-testid="capture-click-input"
					/>
					{errors.click_id ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.click_id}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="capture-aff" className={LABEL}>
						Affiliate id (optional)
					</label>
					<input
						id="capture-aff"
						name="affiliate_id"
						type="text"
						maxLength={128}
						className={INPUT}
						value={affiliateId}
						onChange={(e) => setAffiliateId(e.currentTarget.value)}
						disabled={disabled}
						data-testid="capture-aff-input"
					/>
					{errors.affiliate_id ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.affiliate_id}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="capture-value" className={LABEL}>
						Value (cents, optional)
					</label>
					<input
						id="capture-value"
						name="value_cents"
						type="number"
						min={0}
						step={1}
						className={INPUT}
						value={valueCents}
						onChange={(e) => setValueCents(e.currentTarget.value)}
						disabled={disabled}
						data-testid="capture-value-input"
					/>
					{errors.value_cents ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.value_cents}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="capture-currency" className={LABEL}>
						Currency (optional)
					</label>
					<input
						id="capture-currency"
						name="currency"
						type="text"
						maxLength={3}
						placeholder="usd"
						className={INPUT}
						value={currency}
						onChange={(e) => setCurrency(e.currentTarget.value)}
						disabled={disabled}
						data-testid="capture-currency-input"
					/>
					{errors.currency ? (
						<p className="text-xs text-red-600" role="alert">
							{errors.currency}
						</p>
					) : null}
				</div>

				<div className="sm:col-span-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={disabled}
						data-testid="capture-submit"
					>
						{props.busy ? "Sending." : "Send event"}
					</button>
				</div>
			</form>
		</section>
	);
}

function RecentEventsCard(props: {
	rows: readonly CustomEvent[];
	loading: boolean;
	error: string | null;
}) {
	return (
		<section className={`${CARD} flex flex-col`} aria-label="Recent events">
			<header className="flex flex-col gap-1 border-b border-zinc-200 p-5">
				<h2 className="text-base font-semibold text-zinc-900">
					Recent events
				</h2>
				<p className={HINT}>
					Last {props.rows.length || "0"} captured instances, newest first.
				</p>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-red-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading events.</div>
			) : props.rows.length === 0 ? (
				<div className="flex flex-col items-center gap-1 p-10 text-center">
					<p className="text-sm font-medium text-zinc-700">
						No events captured yet.
					</p>
					<p className="text-xs text-zinc-500">
						Use the capture form above to send your first one.
					</p>
				</div>
			) : (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse text-left text-sm"
						data-testid="events-table"
					>
						<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
							<tr>
								<Th>Event</Th>
								<Th>Value</Th>
								<Th>Click</Th>
								<Th>Affiliate</Th>
								<Th>Occurred</Th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{props.rows.map((row) => (
								<tr
									key={row.id}
									data-testid={`event-row-${row.id}`}
									className="hover:bg-zinc-50/80"
								>
									<Td>
										<code className="font-mono text-zinc-900">
											{row.event_name}
										</code>
									</Td>
									<Td className="text-zinc-700">
										{formatCents(row.value_cents, row.currency)}
									</Td>
									<Td className="text-zinc-600">
										{row.click_id ?? "—"}
									</Td>
									<Td className="text-zinc-600">
										{row.affiliate_id ?? "—"}
									</Td>
									<Td className="text-zinc-600">
										{formatDate(row.occurred_at)}
									</Td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</section>
	);
}

// ---- badges + tiny table primitives --------------------------------------

function StatusBadge(props: { status: "active" | "archived" }) {
	if (props.status === "active") {
		return (
			<span className="inline-flex items-center rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
				Active
			</span>
		);
	}
	return (
		<span className="inline-flex items-center rounded-[6px] border border-zinc-200 bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600">
			Archived
		</span>
	);
}

function CategoryBadge(props: { category: EventCategory }) {
	const palette: Record<EventCategory, string> = {
		ecommerce: "border-green-200 bg-green-50 text-green-700",
		engagement: "border-green-200 bg-green-50 text-green-700",
		auth: "border-zinc-200 bg-zinc-50 text-zinc-700",
		game: "border-zinc-200 bg-zinc-50 text-zinc-700",
		custom: "border-zinc-200 bg-zinc-50 text-zinc-700",
	};
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${palette[props.category]}`}
		>
			{categoryLabel(props.category)}
		</span>
	);
}

function Th(props: { children: React.ReactNode; className?: string }) {
	return (
		<th scope="col" className={`px-4 py-2.5 ${props.className ?? ""}`.trim()}>
			{props.children}
		</th>
	);
}

function Td(props: { children: React.ReactNode; className?: string }) {
	return (
		<td className={`px-4 py-3 align-top text-zinc-700 ${props.className ?? ""}`.trim()}>
			{props.children}
		</td>
	);
}

export { EventTrackingPage as EventTrackingPageNamed };
